import { Navigate } from "react-router-dom";
import SuperAdminRoute from "@/components/auth/SuperAdminRoute";
import { RouteObject } from "@/configs/@types/router";
import { Config } from "@/hooks/useConfig";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import AdminLayout from "@/layouts/AdminLayout";
import VoiceekLayout from "@/layouts/VoiceekLayout";
import AttributeManagementPage from "@/pages/admin/AttributeManagement.page";
import CategoryDownloadPage from "@/pages/admin/categoryManagement/CategoryDownload.page";
import CategoryUploadPage from "@/pages/admin/categoryManagement/CategoryUpload.page";
import DisplayGroupPage from "@/pages/admin/displayGroup/DisplayGroup.page";
import DisplayGroupCreatePage from "@/pages/admin/displayGroup/DisplayGroupCreate.page";
import DisplayGroupEditPage from "@/pages/admin/displayGroup/DisplayGroupEdit.page";
import Error404Page from "@/pages/admin/errors/404.page";
import ExecuteAnalysisPage from "@/pages/admin/ExecuteAnalysis.page";
import ExecuteTagAssignmentPage from "@/pages/admin/ExecuteTagAssignment.page";
import HomeAdmin from "@/pages/admin/HomeAdmin.page";
import PromptEditPage from "@/pages/admin/PromptEdit.page";
import TagCreatePage from "@/pages/admin/tag/TagCreate.page";
import TagEditPage from "@/pages/admin/tag/TagEdit.page";
import TagListPage from "@/pages/admin/tag/TagList.page";
import UserCreatePage from "@/pages/admin/userManagement/UserCreate.page";
import UserEditPage from "@/pages/admin/userManagement/UserEdit.page";
import UserListPage from "@/pages/admin/userManagement/UserList.page";
import VoCFileUploadPage from "@/pages/admin/vocUpload/VoCFileUpload.page";
import VoCUploadHistoryPage from "@/pages/admin/vocUpload/VoCUploadHistory.page";
import WorkspaceCreatePage from "@/pages/admin/workspace/WorkspaceCreate.page";
import WorkspaceEditPage from "@/pages/admin/workspace/WorkspaceEdit.page";
import WorkspaceListPage from "@/pages/admin/workspace/WorkspaceList.page";
import AgentPage from "@/pages/ui/AgentChat.page";
import AgentManagementPage from "@/pages/ui/AgentManagement.page";
import AnalysisPage from "@/pages/ui/Analysis.page";
import CommandFormPage from "@/pages/ui/CommandForm.page";
import ComponentPage from "@/pages/ui/Components.page";
import DashboardPage from "@/pages/ui/Dashboard.page";
import HomePage from "@/pages/ui/Home.page";
import SubAgentFormPage from "@/pages/ui/SubAgentForm.page";
import { useAppStore } from "@/stores/ui/useAppStore";
import { routes, wsRouteParamPlaceholder } from "./routes";

const RedirectToHome = () => {
  const { sessionCurrentWorkspaceId } = useWorkspaces();
  useAppStore(s => s.setIsAgentMode)(false);

  return sessionCurrentWorkspaceId && <Navigate to={routes.top(sessionCurrentWorkspaceId)} replace />;
};

const routerConfigArray = (config: Config | undefined): RouteObject[] => [
  {
    path: "/",
    element: <VoiceekLayout />,
    children: [
      { index: true, element: <RedirectToHome /> },
      {
        path: routes.top(wsRouteParamPlaceholder),
        element: <HomePage />,
      },
      {
        path: routes.analysis(wsRouteParamPlaceholder)(":id"),
        element: <AnalysisPage />,
      },
      {
        path: routes.agent(wsRouteParamPlaceholder),
        element: config?.enableAgent ? <AgentPage /> : <RedirectToHome />,
      },
      {
        path: routes.agentThread(wsRouteParamPlaceholder)(":threadId"),
        element: config?.enableAgent ? <AgentPage /> : <RedirectToHome />,
      },
      {
        path: routes.agentManagement(wsRouteParamPlaceholder),
        element: config?.enableAgent ? <AgentManagementPage /> : <RedirectToHome />,
      },
      {
        path: routes.createCommand(wsRouteParamPlaceholder),
        element: config?.enableAgent ? <CommandFormPage /> : <RedirectToHome />,
      },
      {
        path: routes.editCommand(wsRouteParamPlaceholder)(":commandId"),
        element: config?.enableAgent ? <CommandFormPage /> : <RedirectToHome />,
      },
      {
        path: routes.createSubAgent(wsRouteParamPlaceholder),
        element: config?.enableAgent ? <SubAgentFormPage /> : <RedirectToHome />,
      },
      {
        path: routes.editSubAgent(wsRouteParamPlaceholder)(":subAgentId"),
        element: config?.enableAgent ? <SubAgentFormPage /> : <RedirectToHome />,
      },
      {
        path: "/dashboard",
        element: config?.enableBi ? <DashboardPage /> : <RedirectToHome />,
      },
      {
        path: "/component",
        element: import.meta.env.DEV ? <ComponentPage /> : <RedirectToHome />,
      },
      {
        path: "*",
        element: <RedirectToHome />,
      },
    ],
  },
  {
    path: routes.admin,
    element: <AdminLayout />,
    children: [
      { index: true, element: <HomeAdmin /> },
      {
        path: routes.vocUpload(wsRouteParamPlaceholder).replace("/admin/", ""),
        element: <VoCFileUploadPage />,
      },
      {
        path: routes.vocUploadHistory(wsRouteParamPlaceholder).replace("/admin/", ""),
        element: <VoCUploadHistoryPage />,
      },
      {
        path: routes.execAnalysis(wsRouteParamPlaceholder).replace("/admin/", ""),
        element: <ExecuteAnalysisPage />,
      },
      {
        path: routes.attributeManagement(wsRouteParamPlaceholder).replace("/admin/", ""),
        element: <AttributeManagementPage />,
      },
      {
        path: routes.categoryUpload(wsRouteParamPlaceholder).replace("/admin/", ""),
        element: <CategoryUploadPage />,
      },
      {
        path: routes.categoryDownload(wsRouteParamPlaceholder).replace("/admin/", ""),
        element: <CategoryDownloadPage />,
      },
      {
        path: routes.displayGroup(wsRouteParamPlaceholder).replace("/admin/", ""),
        element: <DisplayGroupPage />,
      },
      {
        path: routes.displayGroupCreate(wsRouteParamPlaceholder).replace("/admin/", ""),
        element: <DisplayGroupCreatePage />,
      },
      {
        path: routes.displayGroupEdit(wsRouteParamPlaceholder)(":id").replace("/admin/", ""),
        element: <DisplayGroupEditPage />,
      },
      {
        path: routes.userList(wsRouteParamPlaceholder).replace("/admin/", ""),
        element: <UserListPage />,
      },
      {
        path: routes.userCreate(wsRouteParamPlaceholder).replace("/admin/", ""),
        element: <UserCreatePage />,
      },
      {
        path: routes.userEdit(wsRouteParamPlaceholder)(":id").replace("/admin/", ""),
        element: <UserEditPage />,
      },
      {
        path: routes.promptEdit(wsRouteParamPlaceholder).replace("/admin/", ""),
        element: (
          <SuperAdminRoute>
            <PromptEditPage />
          </SuperAdminRoute>
        ),
      },
      {
        path: routes.tagList(wsRouteParamPlaceholder).replace("/admin/", ""),
        element: <TagListPage />,
      },
      {
        path: routes.tagCreate(wsRouteParamPlaceholder).replace("/admin/", ""),
        element: <TagCreatePage />,
      },
      {
        path: routes.tagEdit(wsRouteParamPlaceholder)(":tagId").replace("/admin/", ""),
        element: <TagEditPage />,
      },
      {
        path: routes.tagAssignment(wsRouteParamPlaceholder).replace("/admin/", ""),
        element: <ExecuteTagAssignmentPage />,
      },
      {
        path: routes.workspaceList.replace("/admin/", ""),
        element: (
          <SuperAdminRoute>
            <WorkspaceListPage />
          </SuperAdminRoute>
        ),
      },
      {
        path: routes.workspaceCreate.replace("/admin/", ""),
        element: (
          <SuperAdminRoute>
            <WorkspaceCreatePage />
          </SuperAdminRoute>
        ),
      },
      {
        path: routes.workspaceEdit(":id").replace("/admin/", ""),
        element: (
          <SuperAdminRoute>
            <WorkspaceEditPage />
          </SuperAdminRoute>
        ),
      },
      {
        path: "*",
        element: <Error404Page />,
        handle: { hideSideNav: true },
      },
    ],
  },
];

export default routerConfigArray;
